<template>
  <b-modal
    ref="detail-modal"
    title="Chi tiết thay đổi"
    size="xl"
    hide-footer
    scrollable
  >
    <div v-if="onLoading" class="text-center py-4">
      <b-spinner small variant="primary" />
    </div>
    <template v-else-if="detail">
      <dl class="row mb-3">
        <dt class="col-5 col-md-3 col-lg-2">Chương trình</dt>
        <dd class="col-7 col-md-9 col-lg-10 text-break">
          {{ detail.programName }}
          <span
            v-if="detail.isProgramDeleted"
            class="label label-light-dark label-inline ml-1"
            >Đã xóa</span
          >
        </dd>
        <dt class="col-5 col-md-3 col-lg-2">Hành động</dt>
        <dd class="col-7 col-md-9 col-lg-10">{{ actionLabel }}</dd>
        <dt class="col-5 col-md-3 col-lg-2">Người thực hiện</dt>
        <dd class="col-7 col-md-9 col-lg-10">{{ detail.updatedByName || '—' }}</dd>
        <dt class="col-5 col-md-3 col-lg-2">Thời gian</dt>
        <dd class="col-7 col-md-9 col-lg-10">{{ formatTime(detail.createdAt) }}</dd>
      </dl>

      <p v-if="isDelete" class="font-weight-bold mb-0">
        {{ messages.PROGRAM_DELETED }}
      </p>
      <template v-else>
        <template v-if="detail.diffValues.length">
          <h6 class="font-weight-bold">Thông tin chung</h6>
          <b-table
            :items="detail.diffValues"
            :fields="headerFields"
            bordered
            small
            responsive
            class="mb-4"
          >
            <template #cell(originalValue)="{ value }">
              <span class="text-break">{{ value || '' }}</span>
            </template>
            <template #cell(newValue)="{ value }">
              <span class="text-break">{{ value || '' }}</span>
            </template>
          </b-table>
        </template>

        <template v-if="productRows.length">
          <h6 class="font-weight-bold">Sản phẩm áp dụng</h6>
          <b-table
            :items="productRows"
            :fields="productFields"
            :per-page="PRODUCT_PAGE_SIZE"
            :current-page="productPage"
            bordered
            small
            responsive
            class="mb-2"
          >
            <template #cell(productName)="{ value }">
              <span class="text-break">{{ value }}</span>
            </template>
          </b-table>
          <b-pagination
            v-if="productRows.length > PRODUCT_PAGE_SIZE"
            v-model="productPage"
            :total-rows="productRows.length"
            :per-page="PRODUCT_PAGE_SIZE"
            size="sm"
            align="right"
          />
        </template>
        <!-- Lúc Tạo quá 1.000 sản phẩm: API chỉ lưu số lượng -->
        <p v-else-if="detail.detailsCount" class="text-muted mb-0">
          Thêm {{ detail.detailsCount }} sản phẩm
        </p>
      </template>
    </template>
  </b-modal>
</template>

<script>
import moment from 'moment';
import ApiService from '@/core/services/api.service';
import { makeToastFaile } from '@/utils/common';
import { DISCOUNT_HISTORY_MESSAGES } from '@/utils/constants';
import { HISTORY_ACTION_TYPE } from '@/utils/enum';

const PRODUCT_PAGE_SIZE = 50;
const FORBIDDEN = 403;

const ACTION_LABEL = {
  [HISTORY_ACTION_TYPE.CREATE]: 'Tạo chương trình',
  [HISTORY_ACTION_TYPE.UPDATE]: 'Sửa chương trình',
  [HISTORY_ACTION_TYPE.DELETE]: 'Xóa chương trình',
};

// "Trước → Sau" khi giá trị đổi, ngược lại chỉ hiện giá trị hiện tại
const toChange = (prev, next) => (prev !== next ? `${prev || ''} → ${next || ''}` : next);

export default {
  name: 'DiscountProgramHistoryDetail',
  data() {
    return {
      PRODUCT_PAGE_SIZE,
      messages: DISCOUNT_HISTORY_MESSAGES,
      detail: null,
      onLoading: false,
      productPage: 1,
      headerFields: [
        { key: 'keyName', label: 'Thông tin', thStyle: { width: '30%' } },
        { key: 'originalValue', label: 'Trước' },
        { key: 'newValue', label: 'Sau' },
      ],
      productFields: [
        { key: 'change', label: 'Thay đổi', thStyle: { width: '10%' } },
        { key: 'productCode', label: 'Mã SP', tdClass: 'text-nowrap' },
        { key: 'productName', label: 'Tên SP' },
        { key: 'discountTypeName', label: 'Kiểu CK', tdClass: 'text-nowrap' },
        { key: 'discountText', label: 'Chiết khấu', tdClass: 'text-nowrap' },
        { key: 'maxUseText', label: 'Số suất', tdClass: 'text-nowrap' },
      ],
    };
  },
  computed: {
    isDelete() {
      return this.detail && this.detail.action === HISTORY_ACTION_TYPE.DELETE;
    },
    actionLabel() {
      return this.detail ? ACTION_LABEL[this.detail.action] || '' : '';
    },
    productRows() {
      if (!this.detail) return [];
      const { added = [], updated = [], removed = [] } =
        this.detail.detailChanges || {};
      return [
        ...added.map((item) => ({ ...item, change: 'Thêm' })),
        ...updated.map((item) => ({
          ...item,
          change: 'Sửa',
          discountTypeName: toChange(item.prevDiscountTypeName, item.discountTypeName),
          discountText: toChange(item.prevDiscountText, item.discountText),
          maxUseText: toChange(item.prevMaxUseText, item.maxUseText),
        })),
        ...removed.map((item) => ({ ...item, change: 'Xóa' })),
      ];
    },
  },
  methods: {
    formatTime(value) {
      return value ? moment(value).format('HH:mm DD/MM/YYYY') : '';
    },
    async show(historyId) {
      this.detail = null;
      this.productPage = 1;
      this.$refs['detail-modal'].show();
      this.onLoading = true;
      try {
        const { data } = await ApiService.get(`v2/discount-histories/${historyId}`);
        this.detail = data.data;
      } catch (error) {
        const status = error && error.response && error.response.status;
        makeToastFaile(
          status === FORBIDDEN
            ? DISCOUNT_HISTORY_MESSAGES.FORBIDDEN
            : DISCOUNT_HISTORY_MESSAGES.LOAD_FAILED,
        );
        this.$refs['detail-modal'].hide();
      } finally {
        this.onLoading = false;
      }
    },
  },
};
</script>
